// Portal data: /init plus one village/get per world (the first city), polled gently while the Portal is open.
import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { api } from "@/api/client";
import type { Movement, QuestItem, Village } from "@/api/types";
import { keys, ownVillages, presenceVillageId, presenceVillages, useInit } from "@/state/queries";
import { useSession } from "@/state/session";
import { totalIncoming } from "@/game/incoming";
import { useClock } from "@/state/clock";
import { parseGameTime } from "@/game/time";

export interface WorldGlance {
  wid: number;
  name?: string;
  vid: number | null;
  village?: Village;
  loading: boolean;
  error: boolean;
  incoming: number;
  firstLandMs: number | null;
  queueEmpty: boolean;
  cityName: string;
  newReport: boolean;
  newMail: boolean;
  cities: number;
  /** every city id the world's presence lists (the expanded row reads each one's queues) */
  vids: number[];
  kst: number;
  quests: QuestItem[];
  claimable: QuestItem[];
}

import { questList } from "@/game/city";
export { questList };

export function usePortal() {
  const init = useInit();
  const sample = useClock((s) => s.sample);
  const uid = useSession((s) => s.uid);
  const worlds = init.data?.world;
  const names = init.data?.config?.world_names ?? {};
  const inW = useMemo(() => worlds?.worlds_in ?? [], [worlds]);

  const targets = useMemo(
    () =>
      inW.map((wid) => {
        const ids = presenceVillages(worlds, wid).map(presenceVillageId).filter((x): x is number => !!x);
        return { wid, vid: ids[0] ?? null, cities: ids.length, vids: ids };
      }),
    [inW, worlds],
  );

  const queries = useQueries({
    queries: targets.map((t) => ({
      queryKey: keys.village(t.wid, t.vid ?? 0),
      queryFn: async () => {
        const sentAt = Date.now();
        const v = await api.village.get({ wid: t.wid, vid: t.vid! });
        if (typeof v.server_time === "number") sample(v.server_time * 1000, sentAt);
        return v;
      },
      enabled: !!t.vid,
      staleTime: 20_000,
      refetchInterval: 60_000,
      refetchIntervalInBackground: false,
    })),
  });

  const glances: WorldGlance[] = targets.map((t, i) => {
    const q = queries[i];
    const v = q?.data;
    const attacks = (v?.queue_rally?.ra_i ?? []).filter((m: Movement) => m.subtype === "a");
    const lands = attacks.map((m) => parseGameTime(m.end_time)).filter((x) => Number.isFinite(x));
    const quests = questList(v);
    return {
      wid: t.wid,
      name: names[String(t.wid)],
      vid: t.vid,
      village: v,
      loading: !!q?.isLoading,
      error: !!q?.isError,
      // the whole world's incoming (every city in the list), not just the first city's (Oct 15 2026)
      incoming: v ? Math.max(totalIncoming(ownVillages(v, uid)), v.num_incoming_attacks ?? 0, attacks.length) : 0,
      firstLandMs: lands.length ? Math.min(...lands) : null,
      queueEmpty: !!v && (v.queue_hq?.length ?? 0) === 0,
      cityName: v?.village?.city_name ?? "",
      newReport: v?.new_report === 1,
      newMail: v?.new_mail === 1,
      cities: Math.max(t.cities, v ? (Object.values(v.player_details ?? {}).find((p) => (p as { target_char?: { villages?: unknown[] } })?.target_char?.villages) as { target_char: { villages: unknown[] } } | undefined)?.target_char.villages.length ?? 0 : 0),
      vids: t.vids,
      kst: v?.player_stats?.kst ?? 0,
      quests,
      claimable: quests.filter((x) => x.done === 1),
    };
  });

  return { init, glances, loading: init.isLoading, worldsLoaded: !!worlds };
}
